import React, { useContext, useState, useMemo } from "react";
import {
  FormControl,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Stack,
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Chip,
  LinearProgress,
  Avatar,
} from "@mui/material";
import CustomButton from "../../common/Button/CustomButton";
import AllCategoriesContext from "../../../context/allCategories/AllCategoriesContext";
import AllProductsContext from "../../../context/allProducts/AllProductsContext";
import AddCategoryContext from "../../../context/addCategory/AddCategoryContext";
import { getProductsUsingCategory } from "../../../utils/categoryProducts";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

// Firebase
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage } from "../../../firebase";

// Image Libraries
import FileUpload from "../../common/FileUpload";
import CompressAPI from "../../../api/compressImage/CompressAPI";

// Assets
import AddProductImage from "../../../assets/images/placeholder/add-product.jpg";

const AddCategory = ({
  hideModal,
  handleSetCategory,
  action,
  onDeleteCategory,
  products: propsProducts,
}) => {
  const allCategoriesContext = useContext(AllCategoriesContext);
  const allProductsContext = useContext(AllProductsContext);
  const categoryContext = useContext(AddCategoryContext);

  const allCategoriesArr = Array.isArray(allCategoriesContext?.state)
    ? allCategoriesContext.state
    : [];

  const products =
    propsProducts ||
    (Array.isArray(allProductsContext?.state) ? allProductsContext.state : []);

  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [showLinkedProductsModal, setShowLinkedProductsModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const initProImages = action === "EDIT" ? categoryContext.state.images || [] : [];
  const [imageData, setImageData] = useState(initProImages);

  const childCount = useMemo(() => {
    if (!categoryContext.state?.id && !categoryContext.state?.name) return 0;
    return allCategoriesArr.filter(
      (c) =>
        c.parentCategory?.id === categoryContext.state?.id ||
        c.parentCategory?.name === categoryContext.state?.name
    ).length;
  }, [allCategoriesArr, categoryContext.state]);

  const linkedProducts = useMemo(() => {
    if (!categoryContext.state?.id && !categoryContext.state?.name) return [];
    return getProductsUsingCategory(products, categoryContext.state);
  }, [products, categoryContext.state]);

  const hasLinkedProducts = linkedProducts.length > 0;

  const handleConfirmDelete = async () => {
    if (hasLinkedProducts) {
      setOpenConfirmDelete(false);
      return;
    }
    setIsDeleting(true);
    try {
      if (onDeleteCategory) {
        await onDeleteCategory(categoryContext.state);
      }
      setOpenConfirmDelete(false);
      hideModal();
    } catch (err) {
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleChangeParent = (event) => {
    const val = event.target.value;
    if (val === "None") {
      categoryContext.setState({
        ...categoryContext.state,
        parentCategory: {
          id: "None",
          name: "None",
        },
      });
      return;
    }
    const matched = allCategoriesArr.find((cat) => cat.id === val);
    if (matched) {
      categoryContext.setState({
        ...categoryContext.state,
        parentCategory: {
          id: matched.id,
          name: matched.name,
        },
      });
    }
  };

  function handleImageChange(e) {
    setIsUploading(true);
    setProgress(0);
    const upImage = e.target.files[0];
    if (upImage) {
      const compressProps = {
        size: 5,
        quality: 0.8,
        maxWidth: 900,
        maxHeight: 900,
        resize: true,
        rotate: true,
      };
      const imgCompressor = new CompressAPI();
      const req = imgCompressor.compressImage(upImage, compressProps);
      req
        .then((resData) => {
          if (!resData.success) {
            setIsUploading(false);
            return;
          }
          const imgBlob = resData.data;
          setImageData((prevState) => (prevState ? [...prevState, imgBlob] : [imgBlob]));

          const fileName = imgBlob.alt || `category_${Date.now()}`;
          const base64str = imgBlob.data;
          const imgExt = imgBlob.ext;
          const reqData = imgCompressor.base64ToImage(base64str, imgExt);
          if (!reqData.success) {
            setIsUploading(false);
            return;
          }
          const file = reqData.data;
          handleFileUpload({
            path: "images/categories",
            file,
            fileName,
          });
        })
        .catch((ex) => {
          setIsUploading(false);
          console.error("CompressImage Error:", ex);
        });
    }
  }

  const handleFileUpload = ({ path, file, fileName }) => {
    const cleanFileName = fileName.replace(/[()]/g, "");
    const storageRef = ref(storage, `${path}/${cleanFileName}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const prog = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setProgress(prog);
      },
      (error) => {
        setIsUploading(false);
        console.error("Upload Error:", error);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref)
          .then((downloadURL) => {
            setIsUploading(false);
            categoryContext.setState({
              ...categoryContext.state,
              images: [
                ...(categoryContext.state.images || []),
                { name: cleanFileName, url: downloadURL },
              ],
            });
          })
          .catch((ex) => {
            setIsUploading(false);
            console.error("Error getting image URL:", ex);
          });
      }
    );
  };

  const handleDeleteFile = (index, fileName) => {
    const tempImageData = [...imageData];
    tempImageData.splice(index, 1);
    setImageData(tempImageData);
    const fileRef = ref(storage, `images/categories/${fileName}`);
    deleteObject(fileRef)
      .then(() => {
        const updatedURLs = (categoryContext.state.images || []).filter(
          (state) => state.name !== fileName
        );
        categoryContext.setState({
          ...categoryContext.state,
          images: updatedURLs,
        });
      })
      .catch((err) => {
        console.error("Delete file error:", err.message);
      });
  };

  const currentId =
    categoryContext.state?.id ||
    (categoryContext.state?.name || "").trim().replace(/\s+/g, "_").toUpperCase();

  const isFormValid = (categoryContext.state?.name || "").trim().length >= 2;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", maxHeight: "92vh" }}>
      {/* Top Modal Header */}
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2.2,
          borderBottom: "1px solid #f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#ffffff",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              bgcolor: "rgba(0, 77, 64, 0.08)",
              color: "#004d40",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CategoryOutlinedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, color: "#1e293b", fontSize: "1.1rem" }}>
              {action === "ADD" ? "Add New Category" : "Edit Category"}
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              {action === "ADD"
                ? "Configure taxonomy hierarchy and storefront visibility"
                : `Editing: ${categoryContext.state?.name || "Category"}`}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {action === "EDIT" && (
            <Tooltip title={hasLinkedProducts ? "Click to view linked products" : "No products assigned to this category"} arrow>
              <span>
                <Chip
                  icon={<Inventory2OutlinedIcon sx={{ fontSize: "14px !important", color: hasLinkedProducts ? "#ea580c !important" : "#94a3b8 !important" }} />}
                  label={`${linkedProducts.length} linked product${linkedProducts.length === 1 ? "" : "s"}`}
                  size="small"
                  component="button"
                  type="button"
                  clickable
                  aria-haspopup="dialog"
                  aria-controls={showLinkedProductsModal ? "linked-products-dialog" : undefined}
                  onClick={() => setShowLinkedProductsModal(true)}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    bgcolor: hasLinkedProducts ? "#fff7ed" : "#f1f5f9",
                    color: hasLinkedProducts ? "#c2410c" : "#64748b",
                    border: `1px solid ${hasLinkedProducts ? "#fed7aa" : "#e2e8f0"}`,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": hasLinkedProducts
                      ? {
                          bgcolor: "#ffedd5",
                          borderColor: "#fdba74",
                          transform: "translateY(-1px)",
                          boxShadow: "0 2px 6px rgba(194, 65, 12, 0.15)",
                        }
                      : {},
                  }}
                />
              </span>
            </Tooltip>
          )}
          {currentId && (
            <Chip
              label={`#${currentId}`}
              size="small"
              sx={{
                fontWeight: 700,
                fontFamily: "ui-monospace, monospace",
                fontSize: "0.72rem",
                bgcolor: "#f1f5f9",
                color: "#475569",
                display: { xs: "none", sm: "inline-flex" },
              }}
            />
          )}
          <IconButton
            onClick={hideModal}
            size="small"
            sx={{
              color: "#64748b",
              "&:hover": { bgcolor: "#f1f5f9", color: "#1e293b" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Upload Progress Bar */}
      {isUploading && (
        <LinearProgress
          variant="determinate"
          value={progress || 30}
          sx={{
            height: 3,
            bgcolor: "#e2e8f0",
            "& .MuiLinearProgress-bar": { bgcolor: "#10b981" },
          }}
        />
      )}

      {/* Scrollable Form Body */}
      <Box sx={{ p: { xs: 2.5, sm: 3 }, overflowY: "auto", flex: 1 }}>
        <Stack spacing={2.5}>
          {/* Section: Category Info */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              border: "1px solid #e2e8f0",
              borderRadius: 3,
              bgcolor: "#ffffff",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b", mb: 2 }}>
              General Information
            </Typography>

            <Stack spacing={2.5}>
              {/* Category Name */}
              <TextField
                required
                fullWidth
                id="category-name"
                label="Category Name"
                placeholder="e.g. Ayurvedic Oils, Herbal Teas, Skincare..."
                value={categoryContext.state.name || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  categoryContext.setState({
                    ...categoryContext.state,
                    name: val,
                    id: val.trim().replace(/\s+/g, "_").toUpperCase(),
                  });
                }}
                inputProps={{ minLength: 2, maxLength: 50 }}
                helperText={
                  currentId
                    ? `System Slug: #${currentId}`
                    : "Enter a descriptive category name (2-50 characters)"
                }
                FormHelperTextProps={{
                  sx: {
                    fontFamily: currentId ? "ui-monospace, monospace" : "inherit",
                    color: "#64748b",
                    fontSize: "0.75rem",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#f8fafc",
                    borderRadius: 2,
                  },
                }}
              />

              {/* Parent Category Dropdown */}
              <FormControl fullWidth>
                <InputLabel id="parent-category-select-label">Parent Hierarchy</InputLabel>
                <Select
                  labelId="parent-category-select-label"
                  id="parent-category-select"
                  value={categoryContext.state?.parentCategory?.id || "None"}
                  label="Parent Hierarchy"
                  onChange={handleChangeParent}
                  sx={{
                    bgcolor: "#f8fafc",
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="None">
                    <em>None (Top-Level Root Category)</em>
                  </MenuItem>
                  {allCategoriesArr
                    .filter((c) => c.id !== categoryContext.state?.id)
                    .map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Stack>
          </Paper>

          {/* Section: Storefront Visibility */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              border: "1px solid #e2e8f0",
              borderRadius: 3,
              bgcolor: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                Show in Storefront Navbar
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Display this category as a direct link in the top website navigation menu
              </Typography>
            </Box>
            <Switch
              checked={categoryContext.state?.showInNavbar !== false}
              onChange={(e) => {
                categoryContext.setState({
                  ...categoryContext.state,
                  showInNavbar: e.target.checked,
                });
              }}
              color="primary"
            />
          </Paper>

          {/* Section: Category Media */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              border: "1px solid #e2e8f0",
              borderRadius: 3,
              bgcolor: "#ffffff",
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                Category Icon / Banner
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Upload an icon or banner image representing this category (Optional)
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
              {imageData.map((img, index) => {
                const bgImg =
                  img.url !== undefined
                    ? `url(${img.url})`
                    : `url(${img.prefix}${img.data})`;

                return (
                  <Box
                    key={img.name || index}
                    sx={{
                      position: "relative",
                      width: 90,
                      height: 90,
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      backgroundImage: bgImg,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}
                  >
                    <Tooltip title="Delete photo">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (window.confirm("Remove this image?")) {
                            handleDeleteFile(index, img.alt || img.name);
                          }
                        }}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          bgcolor: "rgba(255, 255, 255, 0.9)",
                          color: "#ef4444",
                          p: 0.5,
                          "&:hover": { bgcolor: "#ffffff", color: "#dc2626" },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}

              <FileUpload
                acceptFileType="image/*"
                handleChange={handleImageChange}
                bgImageSrc={AddProductImage}
                upProgress={progress}
              />
            </Box>
          </Paper>
        </Stack>
      </Box>

      {/* Modal Actions Footer */}
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2,
          borderTop: "1px solid #f1f5f9",
          bgcolor: "#ffffff",
          display: "flex",
          flexDirection: { xs: "column-reverse", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Box>
          {action === "EDIT" && onDeleteCategory && (
            <Tooltip
              title={
                hasLinkedProducts
                  ? `Cant delete category: Used by ${linkedProducts.length} product${
                      linkedProducts.length > 1 ? "s" : ""
                    }`
                  : "Delete this category"
              }
              arrow
            >
              <Button
                variant="outlined"
                color={hasLinkedProducts ? "warning" : "error"}
                size="medium"
                startIcon={<DeleteIcon fontSize="small" />}
                onClick={() => setOpenConfirmDelete(true)}
                sx={{
                  fontWeight: 700,
                  borderRadius: "10px",
                  borderColor: hasLinkedProducts ? "#fed7aa" : "#fecaca",
                  color: hasLinkedProducts ? "#c2410c" : "#dc2626",
                  textTransform: "none",
                  px: 2.5,
                  py: 0.9,
                  fontSize: "0.875rem",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    bgcolor: hasLinkedProducts ? "#fff7ed" : "#fef2f2",
                    borderColor: hasLinkedProducts ? "#fb923c" : "#f87171",
                    boxShadow: hasLinkedProducts
                      ? "0 2px 6px rgba(234, 88, 12, 0.12)"
                      : "0 2px 6px rgba(239, 68, 68, 0.12)",
                  },
                }}
              >
                Delete Category
              </Button>
            </Tooltip>
          )}
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end">
          <Button
            variant="outlined"
            size="medium"
            onClick={hideModal}
            sx={{
              fontWeight: 700,
              borderRadius: "10px",
              borderColor: "#cbd5e1",
              color: "#475569",
              textTransform: "none",
              px: 2.5,
              py: 0.9,
              fontSize: "0.875rem",
              transition: "all 0.15s ease",
              "&:hover": {
                bgcolor: "#f8fafc",
                borderColor: "#94a3b8",
              },
            }}
          >
            Cancel
          </Button>

          <CustomButton
            variant="contained"
            color="primary"
            size="medium"
            onClick={() => {
              handleSetCategory(action);
            }}
            disabled={isUploading || !isFormValid}
            sx={{
              fontWeight: 700,
              borderRadius: "10px",
              textTransform: "none",
              px: 3,
              py: 0.9,
              fontSize: "0.875rem",
              bgcolor: "#004d40",
              boxShadow: "0 4px 12px rgba(0, 77, 64, 0.2)",
              "&:hover": {
                bgcolor: "#00382e",
              },
            }}
          >
            {action === "ADD" ? "Add Category" : "Save Changes"}
          </CustomButton>
        </Stack>
      </Box>

      {/* Confirmation / Cant Delete Popup on click of Delete */}
      <Dialog
        open={openConfirmDelete}
        onClose={() => !isDeleting && setOpenConfirmDelete(false)}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 1500 }}
        PaperProps={{
          sx: { borderRadius: "16px", p: 1 },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: "50%",
              bgcolor: hasLinkedProducts ? "#fff7ed" : "#fef2f2",
              color: hasLinkedProducts ? "#ea580c" : "#ef4444",
              display: "flex",
            }}
          >
            <WarningAmberRoundedIcon />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: hasLinkedProducts ? "#9a3412" : "#1e293b",
            }}
          >
            {hasLinkedProducts ? "Cant delete category" : "Delete Category"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {hasLinkedProducts ? (
            <Stack spacing={2}>
              <DialogContentText sx={{ color: "#334155", fontSize: "0.95rem", lineHeight: 1.5 }}>
                Cant delete category <strong>"{categoryContext.state?.name || "this category"}"</strong> because it is currently used by{" "}
                <strong>
                  {linkedProducts.length} product{linkedProducts.length > 1 ? "s" : ""}
                </strong>.
              </DialogContentText>
              <Box
                sx={{
                  p: 1.8,
                  bgcolor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    display: "block",
                    mb: 1,
                  }}
                >
                  Linked Products ({linkedProducts.length}):
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.8,
                    maxHeight: 120,
                    overflowY: "auto",
                  }}
                >
                  {linkedProducts.map((p, idx) => (
                    <Chip
                      key={p.id || idx}
                      label={p.title || p.name || p.id}
                      size="small"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        bgcolor: "#ffffff",
                        border: "1px solid #cbd5e1",
                        color: "#334155",
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                To delete this category, please reassign or remove the category from all associated products first.
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <DialogContentText sx={{ color: "#475569", fontSize: "0.95rem" }}>
                Are you sure you want to delete category{" "}
                <strong>"{categoryContext.state?.name || "this category"}"</strong>? This will permanently remove it from your taxonomy.
              </DialogContentText>
              {childCount > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    bgcolor: "#fffbeb",
                    border: "1px solid #fde68a",
                    borderRadius: "8px",
                    color: "#b45309",
                    fontSize: "0.85rem",
                  }}
                >
                  ⚠️ Warning: <strong>{childCount}</strong> subcategories are currently linked to this category.
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1.5, justifyContent: "flex-end" }}>
          {hasLinkedProducts ? (
            <Button
              variant="contained"
              onClick={() => setOpenConfirmDelete(false)}
              sx={{
                borderRadius: "10px",
                fontWeight: 700,
                textTransform: "none",
                bgcolor: "#004d40",
                px: 3,
                py: 0.9,
                fontSize: "0.875rem",
                boxShadow: "0 2px 8px rgba(0, 77, 64, 0.2)",
                transition: "all 0.15s ease",
                "&:hover": {
                  bgcolor: "#00382e",
                },
              }}
            >
              Understood
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={() => setOpenConfirmDelete(false)}
                disabled={isDeleting}
                sx={{
                  borderRadius: "10px",
                  borderColor: "#cbd5e1",
                  color: "#475569",
                  fontWeight: 700,
                  textTransform: "none",
                  px: 2.5,
                  py: 0.9,
                  fontSize: "0.875rem",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: "#94a3b8",
                    bgcolor: "#f8fafc",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon fontSize="small" />}
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                sx={{
                  borderRadius: "10px",
                  fontWeight: 700,
                  textTransform: "none",
                  bgcolor: "#dc2626",
                  px: 2.8,
                  py: 0.9,
                  fontSize: "0.875rem",
                  boxShadow: "0 2px 8px rgba(220, 38, 38, 0.25)",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    bgcolor: "#b91c1c",
                    boxShadow: "0 4px 12px rgba(220, 38, 38, 0.35)",
                  },
                }}
              >
                {isDeleting ? "Deleting…" : "Delete Category"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Linked Products View Dialog */}
      <Dialog
        id="linked-products-dialog"
        aria-labelledby="linked-products-title"
        open={showLinkedProductsModal}
        onClose={() => setShowLinkedProductsModal(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1500 }}
        PaperProps={{
          sx: { borderRadius: "18px", p: 1, overflow: "hidden" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1.5,
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                bgcolor: "#fff7ed",
                color: "#ea580c",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Inventory2OutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography id="linked-products-title" variant="h6" component="span" sx={{ fontWeight: 800, color: "#1e293b", fontSize: "1.05rem" }}>
                Linked Products
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                {linkedProducts.length} product{linkedProducts.length === 1 ? "" : "s"} currently catalogued under{" "}
                <strong>"{categoryContext.state?.name || "this category"}"</strong>
              </Typography>
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={() => setShowLinkedProductsModal(false)}
            aria-label="Close linked products"
            sx={{
              color: "#94a3b8",
              "&:hover": { color: "#1e293b", bgcolor: "#f1f5f9" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              maxHeight: "380px",
              overflowY: "auto",
              pr: 0.5,
              mt: 1,
            }}
          >
            {!hasLinkedProducts && (
              <Typography variant="body2" sx={{ color: "#64748b", py: 3, textAlign: "center" }}>
                No products are linked to this category yet.
              </Typography>
            )}
            {linkedProducts.map((p, idx) => {
              const productImg =
                Array.isArray(p.images) && p.images.length > 0
                  ? typeof p.images[0] === "string"
                    ? p.images[0]
                    : p.images[0]?.url
                  : p.image || null;

              return (
                <Paper
                  key={p.id || idx}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    bgcolor: "#ffffff",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      bgcolor: "#f8fafc",
                      borderColor: "#cbd5e1",
                    },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <Avatar
                      variant="rounded"
                      src={productImg}
                      alt={p.title || p.name}
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: "#f1f5f9",
                        color: "#94a3b8",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                      }}
                    >
                      <Inventory2OutlinedIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: "#1e293b",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {p.title || p.name || "Untitled Product"}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.3 }}>
                        {p.id && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#64748b",
                              fontFamily: "ui-monospace, monospace",
                              fontSize: "0.72rem",
                            }}
                          >
                            #{p.id}
                          </Typography>
                        )}
                        {p.listPrice && (
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "#047857" }}>
                            ₹{Number(p.listPrice).toLocaleString("en-IN")}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Stack>

                  <Chip
                    label={p.status || (p.isActive !== false ? "Active" : "Draft")}
                    size="small"
                    sx={{
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      height: 22,
                      bgcolor: p.isActive !== false ? "#ecfdf5" : "#f1f5f9",
                      color: p.isActive !== false ? "#047857" : "#64748b",
                      border: `1px solid ${p.isActive !== false ? "#a7f3d0" : "#e2e8f0"}`,
                    }}
                  />
                </Paper>
              );
            })}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #f1f5f9", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={() => setShowLinkedProductsModal(false)}
            sx={{
              borderRadius: "10px",
              fontWeight: 700,
              textTransform: "none",
              bgcolor: "#004d40",
              px: 3,
              py: 0.8,
              fontSize: "0.875rem",
              boxShadow: "0 2px 8px rgba(0, 77, 64, 0.2)",
              "&:hover": { bgcolor: "#00382e" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddCategory;
